import { mat4, vec3, ReadonlyVec3 } from 'gl-matrix';
import { Grid } from '~/engine/data/common/Grid';

export interface WorldSpaceAABB {
    worldMin: vec3;
    worldMax: vec3;
}

/**
 * Flat world-space (patient mm) axis-aligned box. This is the canonical
 * shape of a user-editable volume of interest: the shared VOI overlay's
 * Recoil atom (`voiBoundsState`) aliases it, and engine consumers (body
 * contour, elastix crop) take it as their optional restriction
 * parameter. It differs from {@link WorldSpaceAABB} only in
 * representation (flat numbers instead of a vec3 pair) — flat numbers
 * are what Recoil state and the overlay's per-axis drag math want.
 */
export interface WorldBoundsMm {
    minX: number;
    minY: number;
    minZ: number;
    maxX: number;
    maxY: number;
    maxZ: number;
}

/**
 * Half-open voxel-index window `[min, max)` per axis into a specific
 * grid, produced by {@link gridSubRange}. `min === max` on an axis means
 * the window is empty on that axis (see {@link subRangeIsEmpty}).
 */
/**
 * A region in FRACTIONAL voxel coordinates — where its surface lies,
 * as opposed to {@link VoxelSubRange}, which is the whole-voxel window a
 * pass iterates. Half-open in the same sense: `min` is the low face and
 * `max` the high one, both in voxel units where an integer coordinate is
 * a voxel BOUNDARY and `i + 0.5` is voxel `i`'s centre.
 */
export interface VoxelBounds {
    minX: number;
    minY: number;
    minZ: number;
    maxX: number;
    maxY: number;
    maxZ: number;
}

export interface VoxelSubRange {
    minX: number;
    minY: number;
    minZ: number;
    maxX: number;
    maxY: number;
    maxZ: number;
}

/**
 * Compute the world-space axis-aligned bounding box of a grid by
 * transforming all 8 voxel-space corners through voxelToWorld.
 */
export function computeGridAABB(grid: Grid): WorldSpaceAABB {
    const T = grid.voxelToWorld as mat4;
    const voxel = vec3.create();
    const world = vec3.create();
    const worldMin = vec3.fromValues(+Infinity, +Infinity, +Infinity);
    const worldMax = vec3.fromValues(-Infinity, -Infinity, -Infinity);

    for (let ix = 0; ix <= 1; ix++) {
        for (let iy = 0; iy <= 1; iy++) {
            for (let iz = 0; iz <= 1; iz++) {
                voxel[0] = ix ? grid.sizeX : 0;
                voxel[1] = iy ? grid.sizeY : 0;
                voxel[2] = iz ? grid.sizeZ : 0;
                vec3.transformMat4(world, voxel, T);
                worldMin[0] = Math.min(worldMin[0], world[0]);
                worldMin[1] = Math.min(worldMin[1], world[1]);
                worldMin[2] = Math.min(worldMin[2], world[2]);
                worldMax[0] = Math.max(worldMax[0], world[0]);
                worldMax[1] = Math.max(worldMax[1], world[1]);
                worldMax[2] = Math.max(worldMax[2], world[2]);
            }
        }
    }
    return { worldMin, worldMax };
}

/**
 * The eight corners of a world-mm axis-aligned box, in a fixed order (Z
 * fastest, then Y, then X) — the ONE corner enumeration every projection
 * of a user box goes through: {@link gridSubRange}'s voxel-space window,
 * and the VOI overlay's screen rect and normal-axis interval
 * (`projectVoiBounds`). Sharing it is what makes those projections
 * provably the same point set; the fixed order additionally keeps the
 * reductions over them deterministic. {@link computeGridAABB} walks the
 * corners of a GRID (voxel indices, not mm) with the same nesting.
 */
export function boundsCornersMm(bounds: WorldBoundsMm): vec3[] {
    const corners: vec3[] = [];
    for (const x of [bounds.minX, bounds.maxX]) {
        for (const y of [bounds.minY, bounds.maxY]) {
            for (const z of [bounds.minZ, bounds.maxZ]) {
                corners.push(vec3.fromValues(x, y, z));
            }
        }
    }
    return corners;
}

/**
 * Fraction of the image extent, PER AXIS, that a freshly seeded VOI box
 * covers. 0.5 is registration's long-standing default; sharing the one
 * constant is what keeps the two consumers of the shared VOI overlay
 * (registration, threshold segmentation) from drifting apart. Half the
 * extent on each axis centres a box that is unmistakably smaller than
 * the image on all three planes — a box seeded at the full extent hugs
 * the image edges and reads as "nothing happened".
 */
export const DEFAULT_VOI_EXTENT_FRACTION = 0.5;

/**
 * Centre a box of `fraction` of `aabb`'s extent (per axis) inside it.
 * The seed for a user-editable VOI: see
 * {@link DEFAULT_VOI_EXTENT_FRACTION}. `fraction` is NOT clamped — a
 * caller passing a value outside (0, 1] is a programming error, and
 * silently fixing it would hide a wrong default from the user.
 */
export function centeredBoundsFraction(
    aabb: WorldSpaceAABB,
    fraction: number
): WorldBoundsMm {
    const { worldMin, worldMax } = aabb;
    const center = [0, 1, 2].map((i) => (worldMin[i] + worldMax[i]) / 2);
    const half = [0, 1, 2].map(
        (i) => ((worldMax[i] - worldMin[i]) * fraction) / 2
    );
    return {
        minX: center[0] - half[0],
        minY: center[1] - half[1],
        minZ: center[2] - half[2],
        maxX: center[0] + half[0],
        maxY: center[1] + half[1],
        maxZ: center[2] + half[2],
    };
}

/**
 * Centre a box of a fixed physical extent (`extentMm`, per axis) inside
 * `aabb`, clamped per axis: an image axis shorter than the extent is
 * covered fully rather than overhung. The seed for consumers whose
 * region has a physical meaning independent of the scan size — the SUV
 * tool's box frames ONE lesion, so its opening size is lesion-scaled: a
 * fraction-of-extent seed on a whole-body PET covers most of the torso,
 * whose SUVmax (brain, heart, bladder) is practically never the
 * intended lesion's. Sibling of {@link centeredBoundsFraction}, and like
 * it `extentMm` is NOT validated — a non-positive extent is a
 * programming error, and silently fixing it would hide a wrong default
 * from the user.
 */
export function centeredBoundsMm(
    aabb: WorldSpaceAABB,
    extentMm: number
): WorldBoundsMm {
    const { worldMin, worldMax } = aabb;
    const center = [0, 1, 2].map((i) => (worldMin[i] + worldMax[i]) / 2);
    const half = [0, 1, 2].map(
        (i) => Math.min(extentMm, worldMax[i] - worldMin[i]) / 2
    );
    return {
        minX: center[0] - half[0],
        minY: center[1] - half[1],
        minZ: center[2] - half[2],
        maxX: center[0] + half[0],
        maxY: center[1] + half[1],
        maxZ: center[2] + half[2],
    };
}

/**
 * Translate `bounds` — size preserved on every axis — so its centre lands
 * at `centerMm`, shifted per axis to stay inside `clampAabb` (never
 * shrunk); an axis on which the box exceeds the AABB extent is centred on
 * that axis instead. The double-click recenter of the shared VOI overlay.
 */
export function centerBoundsAt(
    bounds: WorldBoundsMm,
    centerMm: ReadonlyVec3,
    clampAabb: WorldSpaceAABB
): WorldBoundsMm {
    const { worldMin, worldMax } = clampAabb;
    const min = [bounds.minX, bounds.minY, bounds.minZ];
    const max = [bounds.maxX, bounds.maxY, bounds.maxZ];
    const half = [0, 1, 2].map((i) => (max[i] - min[i]) / 2);
    const center = [0, 1, 2].map((i) => {
        const lowest = worldMin[i] + half[i];
        const highest = worldMax[i] - half[i];
        if (lowest > highest) {
            return (worldMin[i] + worldMax[i]) / 2;
        }
        return Math.min(Math.max(centerMm[i], lowest), highest);
    });
    return {
        minX: center[0] - half[0],
        minY: center[1] - half[1],
        minZ: center[2] - half[2],
        maxX: center[0] + half[0],
        maxY: center[1] + half[1],
        maxZ: center[2] + half[2],
    };
}

/**
 * Resolve a world-space box to the voxel-index window of `grid` it
 * covers: ALL 8 corners of the box are mapped to voxel space and their
 * per-axis extremes taken (mirroring {@link computeGridAABB}'s loop, so
 * neither an axis flip nor a genuine grid rotation — DICOM IOP — can
 * crop or invert the window), snapped OUTWARD — floor the low corner,
 * ceil the high one; a voxel partially inside the box is inside — and
 * clamped to the grid. On a rotated grid the window is the voxel-space
 * AABB of the box, so it may over-cover (include voxels outside the
 * rotated box) but never excludes a voxel the box intersects.
 * This is the one place a user box becomes voxels, so every consumer
 * resolves it identically (the rule originated in the elastix VOI crop,
 * `cropImage3DArray`, which has since been deleted as dead code). A box
 * wholly outside the grid clamps to an empty window rather than
 * throwing — the user can legitimately drag the box off the volume.
 * Inverted WORLD
 * bounds (min > max on an axis) do throw: every producer sorts its
 * bounds (see the overlay's ensureValidBounds), so an inverted box here
 * is a programming error, not a value to fix silently.
 */
export function gridSubRange(
    grid: Grid,
    boundsMm: WorldBoundsMm
): VoxelSubRange {
    const exact = gridVoxelBounds(grid, boundsMm);
    const clamp = (value: number, size: number) =>
        Math.min(Math.max(value, 0), size);
    return {
        minX: clamp(Math.floor(exact.minX), grid.sizeX),
        minY: clamp(Math.floor(exact.minY), grid.sizeY),
        minZ: clamp(Math.floor(exact.minZ), grid.sizeZ),
        maxX: clamp(Math.ceil(exact.maxX), grid.sizeX),
        maxY: clamp(Math.ceil(exact.maxY), grid.sizeY),
        maxZ: clamp(Math.ceil(exact.maxZ), grid.sizeZ),
    };
}

/**
 * Fractional voxel-space bounding box of a world box — the SAME corner
 * enumeration {@link gridSubRange} uses, without its outward snap.
 *
 * This is the region the user drew, to the precision they drew it at.
 * The snapped window says which voxels a pass must VISIT, and rounds
 * outward so no partially covered voxel is missed; these bounds say
 * where the region's surface actually LIES, which is what the
 * segmentation must cut at — a structure delivered at the snapped
 * window's edge overshoots the drawn region by up to a voxel per face,
 * and on PET spacing that is millimetres.
 */
export function gridVoxelBounds(
    grid: Grid,
    boundsMm: WorldBoundsMm
): VoxelBounds {
    if (
        boundsMm.minX > boundsMm.maxX ||
        boundsMm.minY > boundsMm.maxY ||
        boundsMm.minZ > boundsMm.maxZ
    ) {
        throw new Error(
            'gridVoxelBounds: bounds must satisfy min <= max on every axis'
        );
    }

    // Voxel-space AABB over all 8 world-box corners ({@link
    // boundsCornersMm}, the enumeration the overlay's projections share).
    // Mapping only the two extreme corners handled axis flips but broke
    // on rotation: the rotated grid's own AABB corners can map to voxel
    // points that agree on an axis, collapsing the window to empty — a
    // box dragged out to the full volume then produced an all-background
    // mask instead of normalizing to "no restriction". (It was the seeded
    // default at the time, so every restricted run on a rotated grid hit
    // this.)
    const lo = vec3.fromValues(+Infinity, +Infinity, +Infinity);
    const hi = vec3.fromValues(-Infinity, -Infinity, -Infinity);
    for (const corner of boundsCornersMm(boundsMm)) {
        const voxel = grid.toVoxelSpace(corner);
        vec3.min(lo, lo, voxel);
        vec3.max(hi, hi, voxel);
    }
    return {
        minX: lo[0],
        minY: lo[1],
        minZ: lo[2],
        maxX: hi[0],
        maxY: hi[1],
        maxZ: hi[2],
    };
}

/** True when the window contains no voxels (empty on some axis). */
export function subRangeIsEmpty(range: VoxelSubRange): boolean {
    return (
        range.minX >= range.maxX ||
        range.minY >= range.maxY ||
        range.minZ >= range.maxZ
    );
}

/**
 * Whole-voxel equality of two windows. Dragging a box emits a new bounds
 * object per pointer event, but consecutive sub-voxel positions snap to
 * the same integer window — callers use this to keep one window identity
 * across those, so memoized O(window) passes re-run only when the voxels
 * they read actually change.
 */
export function subRangeEquals(a: VoxelSubRange, b: VoxelSubRange): boolean {
    return (
        a.minX === b.minX &&
        a.minY === b.minY &&
        a.minZ === b.minZ &&
        a.maxX === b.maxX &&
        a.maxY === b.maxY &&
        a.maxZ === b.maxZ
    );
}

/**
 * The body a region selection occupies within its bounds: the bounds
 * themselves, or the ellipsoid inscribed in them (a sphere while the
 * bounds are a cube). The bounds stay the editable handle geometry and
 * the compute window in both cases — an ellipsoid narrows what counts as
 * inside, never how the region is positioned or resized.
 */
export type VoiShape = 'box' | 'ellipsoid';

/**
 * Half-open x span `[lo, hi)` of the voxels on row `(y, z)` whose CENTRE
 * lies inside `bounds` under `shape`. Empty (`lo === hi`) for a row the
 * region misses.
 *
 * A whole-voxel verdict, right for counting voxels — which is what the
 * region statistics do — and wrong for placing a surface: an iso-surface
 * derived from it can only fall on voxel faces, which on a curved
 * boundary is a staircase. The segmentation cuts with
 * {@link regionInsideDistanceMm} instead, whose sub-voxel field puts the
 * contour where the surface is. The two share a sign at every voxel
 * centre, so a voxel the statistics count is a voxel the structure
 * contains.
 *
 * The span is contiguous under both shapes: a plane cuts a box in a
 * rectangle and an ellipsoid in an ellipse, so a row needs one
 * closed-form interval rather than a per-voxel test.
 *
 * @param slackVoxels Dilation in voxels. The mask phase takes a skin so
 *   the metric cut has real field values on BOTH sides of the true
 *   surface to interpolate between; 0 is the exact figure.
 */
export function regionCenterXSpan(
    bounds: VoxelBounds,
    shape: VoiShape,
    y: number,
    z: number,
    slackVoxels: number = 0
): { lo: number; hi: number } {
    // An empty span still has to name voxel indices inside the bounds:
    // callers clamp against it, and a span anchored at 0 would report a
    // low edge the region does not reach.
    const empty = {
        lo: Math.ceil(bounds.minX - 0.5),
        hi: Math.ceil(bounds.minX - 0.5),
    };
    const minX = bounds.minX - slackVoxels;
    const maxX = bounds.maxX + slackVoxels;
    const minY = bounds.minY - slackVoxels;
    const maxY = bounds.maxY + slackVoxels;
    const minZ = bounds.minZ - slackVoxels;
    const maxZ = bounds.maxZ + slackVoxels;
    if (minX >= maxX || minY >= maxY || minZ >= maxZ) {
        return empty;
    }

    const centerY = y + 0.5;
    const centerZ = z + 0.5;
    if (centerY < minY || centerY > maxY || centerZ < minZ || centerZ > maxZ) {
        return empty;
    }

    // Voxel `x` is in iff minX <= x + 0.5 <= maxX.
    const spanFrom = (lowX: number, highX: number) => {
        const lo = Math.ceil(lowX - 0.5);
        const hi = Math.floor(highX - 0.5) + 1;
        return { lo, hi: Math.max(lo, hi) };
    };

    if (shape === 'box') {
        return spanFrom(minX, maxX);
    }

    const semiX = (maxX - minX) / 2;
    const semiY = (maxY - minY) / 2;
    const semiZ = (maxZ - minZ) / 2;
    const dy = (centerY - (minY + maxY) / 2) / semiY;
    const dz = (centerZ - (minZ + maxZ) / 2) / semiZ;
    // `< 0`, not `<= 0`: a row exactly tangent at the pole has one voxel
    // centre ON the surface, where the distance field reads zero and the
    // `>= iso` cut takes it as inside. Rejecting the row here would have
    // the statistics miss a voxel the segmentation delivers — and an
    // uncounted voxel can only lower the region's maximum, which lowers
    // the resolved threshold and ENLARGES the structure.
    const remaining = 1 - dy * dy - dz * dz;
    if (remaining < 0) {
        return empty;
    }
    const half = semiX * Math.sqrt(remaining);
    const center = (minX + maxX) / 2;
    return spanFrom(center - half, center + half);
}

/**
 * Millimetres the voxel centre `(x, y, z)` lies INSIDE `bounds` under
 * `shape` — negative outside, zero on the surface. The signed-distance
 * form of {@link regionCenterXSpan}, and the one the segmentation cuts
 * with: quantised onto the SDF's byte scale it places the contour
 * between voxel centres, where a whole-voxel verdict could only place it
 * on a voxel face.
 *
 * `resMm` carries the voxel spacing because the distance must be metric:
 * the SDF's byte scale is millimetres, and an anisotropic grid makes a
 * voxel-space sphere a millimetre-space ellipsoid.
 *
 * The ellipsoid uses the standard closed-form approximation — exact for
 * a sphere, and accurate near the surface, which is the only place the
 * value can move a contour: further out the SDF saturates against its
 * own margin. The box form is exact everywhere.
 */
export function regionInsideDistanceMm(
    bounds: VoxelBounds,
    shape: VoiShape,
    resMm: readonly [number, number, number],
    x: number,
    y: number,
    z: number
): number {
    if (
        bounds.minX >= bounds.maxX ||
        bounds.minY >= bounds.maxY ||
        bounds.minZ >= bounds.maxZ
    ) {
        return -Infinity;
    }
    // Offsets from the centre and semi-axes, both in mm.
    const px = (x + 0.5 - (bounds.minX + bounds.maxX) / 2) * resMm[0];
    const py = (y + 0.5 - (bounds.minY + bounds.maxY) / 2) * resMm[1];
    const pz = (z + 0.5 - (bounds.minZ + bounds.maxZ) / 2) * resMm[2];
    const rx = ((bounds.maxX - bounds.minX) / 2) * resMm[0];
    const ry = ((bounds.maxY - bounds.minY) / 2) * resMm[1];
    const rz = ((bounds.maxZ - bounds.minZ) / 2) * resMm[2];

    if (shape === 'box') {
        // Exact box distance: outside, the length of the per-axis
        // overshoot; inside, the nearest face.
        const qx = Math.abs(px) - rx;
        const qy = Math.abs(py) - ry;
        const qz = Math.abs(pz) - rz;
        const outside = Math.hypot(
            Math.max(qx, 0),
            Math.max(qy, 0),
            Math.max(qz, 0)
        );
        const inside = Math.min(Math.max(qx, Math.max(qy, qz)), 0);
        return -(outside + inside);
    }

    const k0 = Math.hypot(px / rx, py / ry, pz / rz);
    const k1 = Math.hypot(px / (rx * rx), py / (ry * ry), pz / (rz * rz));
    if (k1 === 0) {
        // The exact centre: the nearest surface point is along the
        // shortest semi-axis. The ratio form is 0/0 there.
        return Math.min(rx, ry, rz);
    }
    // The approximation is negative inside; report inside-positive, the
    // sense the SDF byte scale encodes. The `+ 0` normalizes the surface
    // to POSITIVE zero: negating an exact zero yields -0, which compares
    // `>= 0` like any zero but reads as outside to `Object.is` and to
    // anyone inspecting the value.
    return -(k0 * (k0 - 1)) / k1 + 0;
}

/**
 * True when the window covers every voxel of `grid`. This is the
 * normalization predicate that makes a full-volume user box equivalent
 * to no box at all — same engine path, same cache key — so a box the
 * user has dragged back out to the whole volume costs nothing and busts
 * no cache. It holds for the BOX shape only: an inscribed ellipsoid
 * excludes the grid's corners however far the bounds are dragged out, so
 * a full-grid ellipsoid is a restriction and must not normalize away.
 * Note this is NOT where a box is seeded (see
 * {@link centeredBoundsFraction}): the opening box is deliberately a
 * sub-region, so enabling the restriction does change the result.
 */
export function subRangeIsFullGrid(grid: Grid, range: VoxelSubRange): boolean {
    return (
        range.minX === 0 &&
        range.minY === 0 &&
        range.minZ === 0 &&
        range.maxX === grid.sizeX &&
        range.maxY === grid.sizeY &&
        range.maxZ === grid.sizeZ
    );
}

/**
 * A grid's world-space box in the flat {@link WorldBoundsMm} shape — the
 * same extent {@link computeGridAABB} reports, in the representation the
 * box helpers and {@link buildTransversalGridForBounds} take. For a rotated grid
 * this is its bounding box, which is larger than the volume inside it.
 */
export function gridBoundsMm(grid: Grid): WorldBoundsMm {
    const { worldMin, worldMax } = computeGridAABB(grid);
    return {
        minX: worldMin[0],
        minY: worldMin[1],
        minZ: worldMin[2],
        maxX: worldMax[0],
        maxY: worldMax[1],
        maxZ: worldMax[2],
    };
}

/**
 * The axis-aligned (identity-rotation, HFS) grid that covers exactly
 * `boundsMm` with the given voxel counts. Voxel size falls out of the box
 * and the counts — `(maxX - minX) / sizeX` per axis — so the realised
 * extent equals the requested one on every axis, with no residual to
 * absorb or hide.
 *
 * The inverse of {@link computeGridAABB}: feeding this grid back through
 * it returns the same box, to float32 — Grid keeps its resolution and
 * transform in Float32Array, so a spacing that is not a dyadic fraction
 * round-trips to within ~1e-5 mm rather than exactly. That round trip is
 * what lets a caller treat the box as the single description of the
 * output geometry rather than keeping a parallel estimate of it; a caller
 * comparing two boxes for equality must allow for that residual (see
 * {@link paddedFractionInBox}).
 *
 * Bounds must be non-degenerate on every axis — a zero-extent box has no
 * voxel size to derive, and silently substituting one would hide a
 * caller's mistake behind a plausible-looking grid.
 */
export function buildTransversalGridForBounds(
    boundsMm: WorldBoundsMm,
    sizeX: number,
    sizeY: number,
    sizeZ: number
): Grid {
    const extentX = boundsMm.maxX - boundsMm.minX;
    const extentY = boundsMm.maxY - boundsMm.minY;
    const extentZ = boundsMm.maxZ - boundsMm.minZ;

    if (extentX <= 0 || extentY <= 0 || extentZ <= 0) {
        throw new Error(
            'buildTransversalGridForBounds: bounds must have a positive extent on every axis'
        );
    }
    // Validated here rather than left to Grid's own assertion, so the
    // message names the box the caller asked for instead of a grid axis
    // one layer down.
    if (
        !Number.isInteger(sizeX) ||
        !Number.isInteger(sizeY) ||
        !Number.isInteger(sizeZ) ||
        sizeX <= 0 ||
        sizeY <= 0 ||
        sizeZ <= 0
    ) {
        throw new Error(
            'buildTransversalGridForBounds: voxel counts must be positive integers'
        );
    }

    // Identity rotation, translated to the corner of voxel [0,0,0] — the
    // translation convention Grid documents for its transformation.
    const cornerAtMin = mat4.create();
    cornerAtMin[12] = boundsMm.minX;
    cornerAtMin[13] = boundsMm.minY;
    cornerAtMin[14] = boundsMm.minZ;

    return new Grid(
        sizeX,
        sizeY,
        sizeZ,
        extentX / sizeX,
        extentY / sizeY,
        extentZ / sizeZ,
        cornerAtMin
    );
}
